import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { map, take, tap, concatMap, skipWhile, catchError } from 'rxjs/operators';
import { validate } from 'uuid';

import { LoginDto, TokenDto, UserState } from '@ebf/core/user/domain';
import { ApiService, HttpMethod } from '@ebf-libs/sdk/platform/api';
import { UsersApiService } from '@ebf/features/users/services/users-api.service';
import { UserDto, UserPermissions } from '@ebf/features/users/domain';
import { EbfToastService } from '@ebf-libs/sdk/platform/toast';
import { ChampionshipsApiService } from '@ebf/features/championships/services/championships-api.service';
import { buildActiveChampionshipUrl, isRootUrl } from '@ebf-libs/sdk/platform/utils';
import { ChampionshipDto } from '@ebf/features/championships/domain';

import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public readonly user$: BehaviorSubject<UserState> = new BehaviorSubject<UserState>({
    user: null,
    token: null,
  });
  private isRefreshing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly apiService: ApiService,
    private readonly usersApiService: UsersApiService,
    private readonly championshipsApiService: ChampionshipsApiService,
    private readonly ebfToastService: EbfToastService,
    private readonly router: Router,
  ) {}

  public login(payload: LoginDto): Observable<any> {
    return this.refreshSession(payload);
  }

  public loginViaMagicLink(email: string, continueUrl: string): Observable<void> {
    return this.apiService.request({
      method: HttpMethod.POST,
      url: '/auth/magic-link',
      body: { email, continueUrl },
    });
  }

  public resetPassword(payload: Pick<UserDto, 'email'>): Observable<void> {
    return this.usersApiService.resetPassword(payload);
  }

  public setToken(token?: TokenDto): void {
    const user = token ? this.getUserSnapshot() : null;
    const tokenData = token || { token: null, refreshToken: null };

    this.user$.next({
      user,
      ...tokenData,
    });

    if (token) {
      localStorage.setItem('token', token.token);
      localStorage.setItem('refreshToken', token.refreshToken);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
  }

  private setUser(user: UserDto): void {
    this.user$.next({
      ...this.user$.getValue(),
      user,
    });
  }

  public logout(force?: boolean, clearQueryParams?: boolean): void {
    this.setToken(null);
    if (!location.pathname.startsWith('/login')) {
      this.router.navigateByUrl(this.getLoginUrl(clearQueryParams), { state: { bypassGuards: force } });
    }
  }

  public getLoginUrl(clearQueryParams?: boolean): string {
    return (
      '/login?backUrl=' + encodeURIComponent(location.pathname + (clearQueryParams ? '' : location.search))
    );
  }

  public refreshSession(payload?: LoginDto): Observable<boolean> {
    if (this.isRefreshing$.getValue()) {
      return this.isRefreshing$.pipe(
        skipWhile(Boolean),
        take(1),
        map(() => true),
      );
    }

    this.isRefreshing$.next(true);
    return this.apiService
      .request({
        method: HttpMethod.POST,
        url: '/auth/token',
        body: payload || { refreshToken: this.getRefreshTokenSnapshot() },
      })
      .pipe(
        tap({
          next: (response: TokenDto) => {
            this.setToken(response);
            this.isRefreshing$.next(false);
          },
          error: () => this.logout(true),
          finalize: () => this.isRefreshing$.next(false),
        }),
        concatMap(() => this.refreshCurrentUser()),
        map(() => true),
      );
  }

  public refreshCurrentUser(): Observable<UserDto> {
    return this.usersApiService.getCurrentUser().pipe(
      concatMap(user => {
        const decodedToken = this.getDecodeToken(this.getTokenSnapshot());
        if (decodedToken?.impersonatedBy) {
          return this.usersApiService.getUserByUuid(decodedToken.impersonatedBy).pipe(
            map(adminUser => ({
              ...user,
              impersonatedBy: adminUser,
            })),
            catchError(() => of(user)),
          );
        }
        return of(user);
      }),
      tap(userWithAdmin => this.setUser(userWithAdmin)),
    );
  }

  public checkSession(): Observable<boolean> {
    const parameters = new URLSearchParams(window.location.search);
    const oobCode: string = parameters.get('oobCode');
    const email: string = parameters.get('email');
    const fbParamsToDelete: string[] = ['apiKey', 'oobCode', 'email', 'mode', 'lang'];
    const isRedirectFromFirebase: boolean = !!(oobCode && email);

    if (isRedirectFromFirebase) {
      return this.login({ email, oobCode }).pipe(
        tap(() => {
          const url: URL = new URL(window.location.href);

          fbParamsToDelete.forEach(param => url.searchParams.delete(param));
          window.history.replaceState(null, '', url);
        }),
        map(() => this.checkRegistrationCompletion()),
        switchMap(isRegistered =>
          isRegistered && isRootUrl() ? this.championshipsApiService.getCurrentChampionship() : of(null),
        ),
        tap(championship => {
          const activeChampionshipUrl = buildActiveChampionshipUrl(championship);
          const { currentChampionshipRedirectEnabled } = environment;

          if (activeChampionshipUrl && currentChampionshipRedirectEnabled) {
            this.router.navigateByUrl(activeChampionshipUrl);
          }
        }),
        map(() => true),
        catchError(() => {
          this.logout(true, true);

          return of(false);
        }),
      );
    }

    const tokenFromStorage = this.getTokenFromStorage();
    const guestUrls = ['/create-model', '/model-data'];

    if (!tokenFromStorage.refreshToken) {
      if (!guestUrls.some(url => location.pathname.startsWith(url))) {
        this.logout();
      }

      return of(false);
    }

    return this.refreshSession(tokenFromStorage).pipe(
      map(() => this.checkRegistrationCompletion()),
      switchMap(registered => {
        console.log(registered, isRootUrl());
        return registered && isRootUrl() ? this.championshipsApiService.getCurrentChampionship() : of(null);
      }),
      tap((championship: ChampionshipDto) => {
        const activeChampionshipUrl = buildActiveChampionshipUrl(championship);
        const { currentChampionshipRedirectEnabled } = environment;

        if (activeChampionshipUrl && currentChampionshipRedirectEnabled) {
          this.router.navigateByUrl(activeChampionshipUrl);
        }
      }),
      map(() => true),
    );
  }

  private checkRegistrationCompletion(): boolean {
    const user: UserDto = this.getUserSnapshot();
    const needCompleteRegistration: boolean = user && !user.uuid && validate(user.uid);

    if (needCompleteRegistration) {
      this.ebfToastService.showError('Please, complete your registration before continue');

      if (!location.pathname.includes('/complete-registration')) {
        this.router.navigateByUrl(
          `/complete-registration?backUrl=${encodeURIComponent(location.pathname + location.search)}`,
        );
      }

      return false;
    }

    return true;
  }

  private getTokenFromStorage(): Pick<UserState, 'token' | 'refreshToken'> {
    return { token: localStorage.getItem('token'), refreshToken: localStorage.getItem('refreshToken') };
  }

  public getUserSnapshot(): UserDto {
    return this.user$.getValue().user;
  }

  public getTokenSnapshot(): any {
    return this.user$.getValue().token;
  }

  public getRefreshTokenSnapshot(): any {
    return this.user$.getValue().refreshToken;
  }

  public isGuest(): boolean {
    return !this.getUserSnapshot();
  }

  public isAdmin(): boolean {
    return !!this.getUserSnapshot()?.admin;
  }

  public isModel(championshipUuid: string): boolean {
    return !!(
      championshipUuid && this.getUserSnapshot()?.permissions?.modelChampionships?.includes(championshipUuid)
    );
  }

  public isContestant(championshipUuid: string): boolean {
    return !!(
      championshipUuid &&
      this.getUserSnapshot()?.permissions?.contestantChampionships?.includes(championshipUuid)
    );
  }

  public isJudge(championshipUuid: string, categoryUuid?: string): boolean {
    const permissions: UserPermissions = this.getUserSnapshot()?.permissions;

    return championshipUuid
      ? permissions?.judgeChampionships?.includes(championshipUuid)
      : permissions?.judgeCategories?.includes(categoryUuid);
  }

  public isResponsible(championshipUuid: string): boolean {
    return !!(
      championshipUuid &&
      this.getUserSnapshot()?.permissions?.responsibleChampionships?.includes(championshipUuid)
    );
  }

  public isEventManager(championshipUuid: string): boolean {
    return !!(
      championshipUuid &&
      this.getUserSnapshot()?.permissions?.eventManagerChampionships?.includes(championshipUuid)
    );
  }

  public isImpersonated(): boolean {
    const decodeToken = this.getDecodeToken(this.getTokenSnapshot());
    return !!decodeToken?.impersonatedBy;
  }

  public isContentManager(championshipUuid: string): boolean {
    return !!(
      championshipUuid &&
      this.getUserSnapshot()?.permissions?.contentManagerChampionships?.includes(championshipUuid)
    );
  }

  public isTokenExpired(token: string): boolean {
    const decodedToken = this.getDecodeToken(token);
    return decodedToken ? new Date() > new Date(+`${decodedToken.exp}000`) : true;
  }

  public impersonateUser(userUuid: string) {
    this.isRefreshing$.next(true);
    return this.apiService
      .request({
        method: HttpMethod.POST,
        url: '/auth/impersonation/create',
        body: { userUuid },
      })
      .pipe(
        tap({
          next: response => {
            this.setToken(response);
            this.isRefreshing$.next(false);
          },
          error: () => this.logout(true),
          finalize: () => this.isRefreshing$.next(false),
        }),
        concatMap(() => this.refreshCurrentUser()),
        map(() => true),
      );
  }

  public endImpersonation() {
    this.isRefreshing$.next(true);
    return this.apiService
      .request({
        method: HttpMethod.POST,
        url: '/auth/impersonation/end',
        body: {},
      })
      .pipe(
        tap({
          next: response => {
            this.setToken(response);
            this.isRefreshing$.next(false);
          },
          error: () => this.logout(true),
          finalize: () => this.isRefreshing$.next(false),
        }),
        concatMap(() => this.refreshCurrentUser()),
        map(() => true),
      );
  }

  private getDecodeToken(token: string) {
    try {
      return token
        ? JSON.parse(
            decodeURIComponent(
              atob(token.split('.')[1].replace('-', '+').replace('_', '/'))
                .split('')
                .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
                .join(''),
            ),
          )
        : null;
    } catch (e) {
      console.log('Token parsing error');
      return null;
    }
  }
}
